export type Lang = 'pl' | 'en';

const translations: Record<string, Record<Lang, string>> = {
  // General
  'app.title': {
    pl: 'Insolitum Universe — Test Urządzeń',
    en: 'Insolitum Universe — Device Testing',
  },
  'app.subtitle': {
    pl: 'Narzędzie diagnostyczne',
    en: 'Diagnostic Tool',
  },

  // Device modes
  'mode.tv': { pl: 'Tryb TV', en: 'TV Mode' },
  'mode.mobile': { pl: 'Tryb Mobile', en: 'Mobile Mode' },
  'mode.switch': { pl: 'Przełącz tryb', en: 'Switch mode' },

  // Navigation
  'nav.overview': { pl: 'Przegląd', en: 'Overview' },
  'nav.summary': { pl: 'Podsumowanie i werdykt', en: 'Summary & Verdict' },

  // Overview
  'overview.title': { pl: 'Test wydajności urządzenia', en: 'Device Performance Test' },
  'overview.desc': {
    pl: 'Uruchom testy aby ocenić czy to urządzenie nadaje się do obsługi aplikacji Insolitum Universe.',
    en: 'Run tests to evaluate whether this device is suitable for running Insolitum Universe applications.',
  },
  'overview.tvDesc': {
    pl: 'Testy TV sprawdzają rendering, animacje CSS, obliczenia JS, pamięć, Canvas 2D, WebGL, obsługę zdarzeń i parsowanie danych.',
    en: 'TV tests check rendering, CSS animations, JS computations, memory, Canvas 2D, WebGL, event handling, and data parsing.',
  },
  'overview.mobileDesc': {
    pl: 'Testy mobilne sprawdzają dotyk, viewport, przewijanie, API multimediów, baterię, PWA, formularze i sieć.',
    en: 'Mobile tests check touch, viewport, scrolling, media APIs, battery, PWA, forms, and network.',
  },
  'overview.fullscreen': { pl: 'Pełny ekran', en: 'Fullscreen' },
  'overview.warmup': { pl: 'Rozgrzewka', en: 'Warm-up' },
  'overview.runAll': { pl: 'Uruchom wszystkie testy', en: 'Run All Tests' },
  'overview.running': { pl: 'Uruchamianie…', en: 'Running…' },
  'overview.envTitle': { pl: 'Wykryte środowisko', en: 'Detected Environment' },
  'overview.warning': {
    pl: '⚠️ Dla realistycznych wyników: zamknij inne aplikacje, uruchom w fullscreen.',
    en: '⚠️ For realistic results: close other apps, run in fullscreen.',
  },
  'overview.tvHint': { pl: 'Typ urządzenia', en: 'Device type' },

  // TV Tests
  'tv.list.title': { pl: '1) Rendering dużej listy', en: '1) Large List Rendering' },
  'tv.list.desc': {
    pl: 'Symuluje długie listy/tabele (2000–5000 wierszy). Mierzy initial render, TTI i koszt re-renderu przy filtrze.',
    en: 'Simulates large lists/tables (2,000–5,000 rows). Measures initial render, TTI, and filter re-render cost.',
  },
  'tv.css.title': { pl: '2) Animacje CSS i FPS', en: '2) CSS Animations & FPS' },
  'tv.css.desc': {
    pl: 'Dużo równoległych animacji (transform/opacity) + parallax. Na TV to często wąskie gardło kompozytora.',
    en: 'Many parallel animations (transform/opacity) + parallax. On TV this is often the compositor bottleneck.',
  },
  'tv.compute.title': { pl: '3) Ciężkie obliczenia JS', en: '3) Heavy JS Computations' },
  'tv.compute.desc': {
    pl: 'Celowo blokujące obliczenia (sorty, sieve, fib). Ocenia moc CPU.',
    en: 'Intentionally blocking computations (sorts, sieve, fib). Evaluates CPU power.',
  },
  'tv.memory.title': { pl: '4) Stress test pamięci', en: '4) Memory Stress Test' },
  'tv.memory.desc': {
    pl: 'Alokacje + graf obiektów. Sprawdza GC i stabilność heapa.',
    en: 'Allocations + object graph. Tests GC and heap stability.',
  },
  'tv.canvas.title': { pl: '5) Canvas 2D', en: '5) Canvas 2D Drawing' },
  'tv.canvas.desc': {
    pl: 'System cząsteczek na Canvas 2D — CPU + rasteryzacja.',
    en: 'Particle system on Canvas 2D — CPU + rasterization.',
  },
  'tv.webgl.title': { pl: '6) Testy WebGL', en: '6) WebGL Performance' },
  'tv.webgl.desc': {
    pl: 'Scena 3D z kostkami, cząsteczkami i oświetleniem. Sprawdza GPU i stabilność WebGL.',
    en: '3D scene with cubes, particles, and lighting. Tests GPU and WebGL stability.',
  },
  'tv.events.title': { pl: '7) Obsługa zdarzeń', en: '7) Event Handling & Input' },
  'tv.events.desc': {
    pl: 'Szybkie eventy + duży formularz. Sprawdza responsywność UI.',
    en: 'Rapid events + large form. Tests UI responsiveness.',
  },
  'tv.network.title': { pl: '8) Ładowanie i parsowanie danych', en: '8) Data Loading & Parsing' },
  'tv.network.desc': {
    pl: 'Generuje duży JSON, robi stringify+parse i renderuje tabelę.',
    en: 'Generates large JSON, does stringify+parse, and renders a table.',
  },

  // Mobile Tests
  'mobile.touch.title': { pl: '1) Responsywność dotyku', en: '1) Touch Responsiveness' },
  'mobile.touch.desc': {
    pl: 'Multi-touch, opóźnienie zdarzeń dotykowych, rozpoznawanie gestów.',
    en: 'Multi-touch, touch event latency, gesture recognition.',
  },
  'mobile.viewport.title': { pl: '2) Viewport i orientacja', en: '2) Viewport & Orientation' },
  'mobile.viewport.desc': {
    pl: 'Wymiary viewport, zmiana orientacji, safe-area, Visual Viewport API.',
    en: 'Viewport dimensions, orientation change, safe-area, Visual Viewport API.',
  },
  'mobile.scroll.title': { pl: '3) Wydajność przewijania', en: '3) Scroll Performance' },
  'mobile.scroll.desc': {
    pl: 'Przewijanie złożonej treści z pomiarem FPS, overscroll, scroll-snap.',
    en: 'Complex content scrolling with FPS measurement, overscroll, scroll-snap.',
  },
  'mobile.media.title': { pl: '4) API multimediów', en: '4) Media APIs' },
  'mobile.media.desc': {
    pl: 'Dostęp do kamery/mikrofonu, czujniki urządzenia, geolokalizacja.',
    en: 'Camera/mic access, device sensors, geolocation.',
  },
  'mobile.battery.title': { pl: '5) Bateria i termika', en: '5) Battery & Thermal' },
  'mobile.battery.desc': {
    pl: 'Battery API, detekcja throttlingu pod obciążeniem CPU.',
    en: 'Battery API, throttle detection under sustained CPU load.',
  },
  'mobile.pwa.title': { pl: '6) Możliwości PWA', en: '6) PWA Capabilities' },
  'mobile.pwa.desc': {
    pl: 'Service Worker, manifest, instalacja, powiadomienia, persistent storage.',
    en: 'Service Worker, manifest, install prompt, notifications, persistent storage.',
  },
  'mobile.form.title': { pl: '7) Formularze i klawiatura', en: '7) Forms & Keyboard' },
  'mobile.form.desc': {
    pl: 'Klawiatura wirtualna, typy inputów, autofill, Visual Viewport.',
    en: 'Virtual keyboard, input types, autofill, Visual Viewport.',
  },
  'mobile.network.title': { pl: '8) Sieć i łączność', en: '8) Network & Connectivity' },
  'mobile.network.desc': {
    pl: 'Connection API, przepustowość, detekcja offline.',
    en: 'Connection API, throughput, offline detection.',
  },

  // Controls
  'btn.start': { pl: 'Start', en: 'Start' },
  'btn.stop': { pl: 'Stop & Wynik', en: 'Stop & Score' },
  'btn.reset': { pl: 'Reset', en: 'Reset' },

  // Metrics
  'metric.fps': { pl: 'Wygładzony FPS', en: 'Smoothed FPS' },
  'metric.quality': { pl: 'Jakość', en: 'Quality' },
  'metric.initialRender': { pl: 'Initial render', en: 'Initial render' },
  'metric.tti': { pl: 'Time to interactive', en: 'Time to interactive' },
  'metric.filterRerender': { pl: 'Filter re-render', en: 'Filter re-render' },
  'metric.itemsRendered': { pl: 'Wyrenderowane elementy', en: 'Items rendered' },
  'metric.heapUsed': { pl: 'Heap used', en: 'Heap used' },
  'metric.peakHeap': { pl: 'Peak heap', en: 'Peak heap' },
  'metric.heapGrowth': { pl: 'Heap growth', en: 'Heap growth' },
  'metric.drawCalls': { pl: 'Draw calls/klatka', en: 'Draw calls/frame' },
  'metric.frameTime': { pl: 'Czas klatki', en: 'Frame time' },
  'metric.eventsPerSec': { pl: 'Zdarzenia/s', en: 'Events/sec' },
  'metric.rendersPerSec': { pl: 'Rendery/s', en: 'Renders/sec' },
  'metric.handlerLag': { pl: 'Opóźnienie handler', en: 'Handler lag' },
  'metric.stringify': { pl: 'Stringify', en: 'Stringify' },
  'metric.parse': { pl: 'Parse', en: 'Parse' },
  'metric.renderSlice': { pl: 'Render slice', en: 'Render slice' },
  'metric.jsonSize': { pl: 'Rozmiar JSON', en: 'JSON size' },

  // Quality labels
  'quality.good': { pl: 'DOBRY', en: 'GOOD' },
  'quality.ok': { pl: 'OK', en: 'OK' },
  'quality.poor': { pl: 'SŁABY', en: 'POOR' },

  // Summary
  'summary.title': { pl: 'Podsumowanie i werdykt', en: 'Summary & Verdict' },
  'summary.desc': {
    pl: 'Wynik jest heurystyczny (0–100). Najważniejsze: stabilność FPS, render/parse, pamięć i WebGL.',
    en: 'Score is heuristic (0–100). Key factors: FPS stability, render/parse, memory, and WebGL.',
  },
  'summary.overall': { pl: 'Wynik ogólny', en: 'Overall Score' },
  'summary.verdict': { pl: 'Werdykt', en: 'Verdict' },
  'summary.completed': { pl: 'Ukończone', en: 'Completed' },
  'summary.test': { pl: 'Test', en: 'Test' },
  'summary.score': { pl: 'Wynik', en: 'Score' },
  'summary.result': { pl: 'Główny wynik', en: 'Primary result' },
  'summary.notRun': { pl: 'Nie uruchomiony', en: 'Not run' },

  // Verdicts
  'verdict.excellent': {
    pl: 'DOSKONAŁY — Urządzenie w pełni nadaje się do aplikacji Universe',
    en: 'EXCELLENT — Device fully suitable for Universe apps',
  },
  'verdict.good': {
    pl: 'DOBRY — Działa dobrze, drobne optymalizacje mogą pomóc',
    en: 'GOOD — Works well, minor optimizations may help',
  },
  'verdict.acceptable': {
    pl: 'AKCEPTOWALNY — Używalne z ostrożną optymalizacją',
    en: 'ACCEPTABLE — Usable with careful optimization',
  },
  'verdict.poor': {
    pl: 'SŁABY — Nie zalecane dla aplikacji Universe',
    en: 'POOR — Not recommended for Universe apps',
  },
  'verdict.noData': { pl: 'Brak danych', en: 'No data' },

  // Settings
  'label.mode': { pl: 'Tryb', en: 'Mode' },
  'label.count': { pl: 'Ilość', en: 'Count' },
  'label.filter': { pl: 'Filtr', en: 'Filter' },
  'label.particles': { pl: 'Cząsteczki', en: 'Particles' },
  'label.elements': { pl: 'Elementy', en: 'Elements' },
  'label.chunkMB': { pl: 'Chunk MB', en: 'Chunk MB' },
  'label.iterations': { pl: 'Iteracje', en: 'Iterations' },
  'label.interval': { pl: 'Interwał (ms)', en: 'Interval (ms)' },
  'label.scene': { pl: 'Scena', en: 'Scene' },
  'label.cubes': { pl: 'Kostki', en: 'Cubes' },
  'label.inputs': { pl: 'Ilość inputów', en: 'Input count' },
  'label.eventsRate': { pl: 'Zdarzenia/s', en: 'Events/sec' },
  'label.records': { pl: 'Rekordy', en: 'Records' },
  'label.fibN': { pl: 'Fib N', en: 'Fib N' },
  'label.sortN': { pl: 'Sort N', en: 'Sort N' },
  'label.primeLimit': { pl: 'Limit liczb pierwszych', en: 'Prime limit' },

  // Options
  'opt.virtual': { pl: 'Virtualized (windowing)', en: 'Virtualized (windowing)' },
  'opt.fullDom': { pl: 'Full DOM (cięższy)', en: 'Full DOM (heavier)' },
  'opt.cycle': { pl: 'Twórz i usuwaj (cykl)', en: 'Create & destroy (cycle)' },
  'opt.leak': { pl: 'Celowy wyciek', en: 'Intentional leak' },
  'opt.simple': { pl: 'Prosta kostka', en: 'Simple cube' },
  'opt.complex': { pl: 'Złożona scena', en: 'Complex scene' },

  // Hints
  'hint.fpsGood': { pl: 'GOOD ≥45, OK 30–45, POOR <30', en: 'GOOD ≥45, OK 30–45, POOR <30' },
  'hint.renderGood': { pl: 'GOOD < 900ms, OK < 1.8s', en: 'GOOD < 900ms, OK < 1.8s' },
  'hint.filterGood': { pl: 'GOOD < 140ms, OK < 350ms', en: 'GOOD < 140ms, OK < 350ms' },
  'hint.parseGood': { pl: 'GOOD < 400ms, OK < 900ms', en: 'GOOD < 400ms, OK < 900ms' },
  'hint.ttiHigh': { pl: 'Wysokie TTI = wolne reakcje UI', en: 'High TTI = slow UI responses' },

  // Touch test
  'touch.area': { pl: 'Dotknij/swipe w tym obszarze', en: 'Touch/swipe in this area' },
  'touch.maxPoints': { pl: 'Maks. punktów dotyku', en: 'Max touch points' },
  'touch.avgLatency': { pl: 'Średnie opóźnienie', en: 'Avg latency' },
  'touch.gestures': { pl: 'Rozpoznane gesty', en: 'Gestures recognized' },
  'touch.events': { pl: 'Zdarzenia dotykowe', en: 'Touch events' },

  // Viewport test
  'viewport.width': { pl: 'Szerokość', en: 'Width' },
  'viewport.height': { pl: 'Wysokość', en: 'Height' },
  'viewport.dpr': { pl: 'Pixel ratio', en: 'Pixel ratio' },
  'viewport.orientation': { pl: 'Orientacja', en: 'Orientation' },
  'viewport.safeArea': { pl: 'Safe area insets', en: 'Safe area insets' },
  'viewport.visualApi': { pl: 'Visual Viewport API', en: 'Visual Viewport API' },

  // Scroll test
  'scroll.fps': { pl: 'FPS podczas scroll', en: 'Scroll FPS' },
  'scroll.smoothness': { pl: 'Płynność', en: 'Smoothness' },
  'scroll.overscroll': { pl: 'Overscroll behavior', en: 'Overscroll behavior' },

  // Media test
  'media.camera': { pl: 'Kamera', en: 'Camera' },
  'media.microphone': { pl: 'Mikrofon', en: 'Microphone' },
  'media.orientation': { pl: 'DeviceOrientation', en: 'DeviceOrientation' },
  'media.motion': { pl: 'DeviceMotion', en: 'DeviceMotion' },
  'media.geolocation': { pl: 'Geolokalizacja', en: 'Geolocation' },

  // Battery test
  'battery.api': { pl: 'Battery API', en: 'Battery API' },
  'battery.level': { pl: 'Poziom baterii', en: 'Battery level' },
  'battery.charging': { pl: 'Ładowanie', en: 'Charging' },
  'battery.throttle': { pl: 'Detekcja throttlingu', en: 'Throttle detection' },

  // PWA test
  'pwa.sw': { pl: 'Service Worker', en: 'Service Worker' },
  'pwa.manifest': { pl: 'Manifest', en: 'Manifest' },
  'pwa.notifications': { pl: 'Powiadomienia', en: 'Notifications' },
  'pwa.storage': { pl: 'Persistent Storage', en: 'Persistent Storage' },
  'pwa.install': { pl: 'Instalacja', en: 'Install prompt' },

  // Form test
  'form.keyboard': { pl: 'Klawiatura wirtualna', en: 'Virtual keyboard' },
  'form.inputTypes': { pl: 'Typy inputów', en: 'Input types' },
  'form.autofill': { pl: 'Autofill', en: 'Autofill' },

  // Network cap test
  'netcap.type': { pl: 'Typ połączenia', en: 'Connection type' },
  'netcap.downlink': { pl: 'Downlink (Mbps)', en: 'Downlink (Mbps)' },
  'netcap.rtt': { pl: 'RTT (ms)', en: 'RTT (ms)' },
  'netcap.offline': { pl: 'Detekcja offline', en: 'Offline detection' },
  'netcap.throughput': { pl: 'Przepustowość', en: 'Throughput' },

  // Status
  'status.idle': { pl: 'Oczekiwanie', en: 'Idle' },
  'status.running': { pl: 'Uruchomiony', en: 'Running' },
  'status.done': { pl: 'Zakończony', en: 'Done' },
  'status.autoRun': { pl: 'AUTO-RUN…', en: 'AUTO-RUN…' },
  'status.pressStart': { pl: 'Naciśnij Start.', en: 'Press Start.' },

  // Misc
  'supported.yes': { pl: 'TAK', en: 'YES' },
  'supported.no': { pl: 'NIE', en: 'NO' },
  'supported.available': { pl: 'Dostępne', en: 'Available' },
  'supported.unavailable': { pl: 'Niedostępne', en: 'Unavailable' },
  'supported.partial': { pl: 'Częściowo', en: 'Partial' },
};

export function t(key: string, lang: Lang): string {
  const entry = translations[key];
  if (!entry) return key;
  return entry[lang] || entry['en'] || key;
}
